import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import _ from 'lodash';

import { Ticket } from '../../../../../api-client';
import { RouterSettings } from '../../../../../utils/router';
import { TRANSLATION_KEYS } from './localization/translation-keys';

import {
    IAIDetailsList,
    IAIContextualMenuColumn,
    IAIDatetimeColumn,
    IAIPreviewColumn,
    IAIPriorityColumn,
    IAIStatusColumn,
    IAIStringColumn
} from '../../../../../common/components/IAITable';
import { ContextualMenuItemType, IColumn, IContextualMenuItem } from '@fluentui/react';
import { useTicketsPivot } from '../TicketsPivot/hooks/useTicketsPivot';
import { useBoolean } from '@fluentui/react-hooks';
import ChangeLogsDrawer from '../TicketsPivot/components/ChangeLogsDrawer/change-logs-drawer';
import DeleteDialog from '../TicketsPivot/components/DeleteDialog/delete-dialog';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/application/redux-store';
import IAIColumnsRefinementDrawer from '../../../../../common/components/IAIColumnsRefinementDrawer/iai-columns-refinements-drawer';
import { ITicketColumn } from '../../../../../common/components/IAITable/IAIDetailsList/iai-details-list';
import IAISolvedStatusColumn from '../../../../../common/components/IAITable/IAISolvedStatusColumn/iai-solved-status-column';

interface ICustomViewTicketsPivot {
    dataSource: Ticket[];
    dataLoading?: boolean;
    deleteTicketLoading?: boolean;
    onDeleteTicket: (id: string, onSuccess: () => void) => void;
    retrieveCallback: () => void;
    onExportTicket?: () => void;
}

const CustomViewTicketsPivot: React.FunctionComponent<ICustomViewTicketsPivot> = ({
    dataSource,
    dataLoading,
    deleteTicketLoading,
    onDeleteTicket,
    retrieveCallback,
    onExportTicket
}) => {
    const iaiAccount = useSelector((state: RootState) => state.account);
    const {
        handleRetrieveChangeLogs,
        retrieveChangeLogs,
        retrieveChangeLogsLoading
    } = useTicketsPivot();

    const [changeLogsDrawerVisible, { toggle: toggleChangeLogsDrawerVisible }] = useBoolean(false);
    const [deleteTicketModalVisible, { toggle: toggleDeleteTicketModalVisible }] = useBoolean(false);
    const [delegateTicket, setDelegateTicket] = useState<Ticket>();
    const [columnsRefinementDrawerVisibile, { toggle: toggleColumnsRefinementDrawerVisibile }] = useBoolean(false);

    const _handleSearchPredicate = (dataItem: Ticket, searchKey: string) => (
        _.includes(dataItem.id?.toLowerCase(), searchKey) ||
        _.includes(dataItem.subject?.toLowerCase(), searchKey) ||
        _.includes(dataItem.description?.toLowerCase(), searchKey) ||
        _.includes(dataItem.requestedBy?.toLowerCase(), searchKey)
    );

    //#region Form-Management

    const navigate = useNavigate();

    const _handleCreateAction = () => {
        navigate(RouterSettings.PATHS.dashboard.ticket.edit('00-0000'));
    }

    //#endregion

    const _handleRetrieveChangeLogs = (id: string) => {
        handleRetrieveChangeLogs({
            id: id,
            onRetrieveSuccess: (changeLogs) => toggleChangeLogsDrawerVisible()
        });
    }

    const _handleDeleteAction = (dataItem: Ticket) => {
        setDelegateTicket(dataItem);
        toggleDeleteTicketModalVisible();
    }

    const _handleDismissDeleteAction = () => {
        setDelegateTicket(undefined);
        toggleDeleteTicketModalVisible();

        retrieveCallback();
    }

    const _handleDeleteTicket = () => {
        if (delegateTicket?.id) {
            onDeleteTicket(delegateTicket.id, _handleDismissDeleteAction);
        }
    }

    const _handleMenuItemsByRole = (dataItem: Ticket, dataIndex?: number): IContextualMenuItem[] => {
        let menuItems: IContextualMenuItem[] = [
            {
                key: `menuItem__edit_${dataIndex}`,
                iconProps: { iconName: 'EditCreate' },
                text: translate(TRANSLATION_KEYS.ColumnActionCommandEditLabel),
                title: translate(TRANSLATION_KEYS.ColumnActionCommandEditTooltip),
                onClick: () => navigate(RouterSettings.PATHS.dashboard.ticket.edit(dataItem.id!))
            }
        ];

        if (iaiAccount.role === 'administrator') {
            menuItems.push({
                key: `menuItem__changeLogs_${dataIndex}`,
                iconProps: { iconName: 'ViewAll' },
                text: translate(TRANSLATION_KEYS.ColumnActionCommandChangeLogsLabel),
                title: translate(TRANSLATION_KEYS.ColumnActionCommandChangeLogsTooltip),
                onClick: () => _handleRetrieveChangeLogs(dataItem.id!)
            });
        }

        menuItems.push({
            key: `menuItem__divider_${dataIndex}`,
            itemType: ContextualMenuItemType.Divider
        });

        if (dataItem.status != 'deleted' &&
            (iaiAccount.role === 'administrator' || iaiAccount.role === 'departmentManager')) {
            menuItems.push({
                key: `menuItem__delete_${dataIndex}`,
                iconProps: { iconName: 'Delete' },
                text: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteLabel),
                title: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteTooltip),
                onClick: () => _handleDeleteAction(dataItem)
            });
        }

        return menuItems;
    }

    const {
        t: translate
    } = useTranslation();

    const properties = useSelector((state: RootState) => state.system.customAttributes);
    const [activeColumns, setActiveColumns] = useState<ITicketColumn[]>([]);

    const propertiesColumns = () => {
        return _.map(properties, dataItem => ({
            key: `tck__Property_${dataItem.id}`,
            name: dataItem.displayName,
            minWidth: 100,
            isResizable: true,
            onRender: (dataSourceItem: Ticket) => (
                <IAIStringColumn align="start" text={dataSourceItem?.properties?.find(x => x.id === dataItem.id)?.value} />
            ),
            isActive: dataItem.defaultRender
        } as IColumn))
    };

    const dataColumns = useMemo(() => {
        return [
            {
                key: 'tck__preview',
                name: '',
                minWidth: 25,
                maxWidth: 25,
                onRender: (dataItem: Ticket) => (
                    <IAIPreviewColumn
                        heading={dataItem.subject}
                        content={parse(dataItem.description || '')}
                        tooltip={translate(TRANSLATION_KEYS.ColumnPreviewTooltip)}
                    />
                ),
                isLocked: true,
            },
            {
                key: 'tck__subject',
                name: translate(TRANSLATION_KEYS.ColumnTitleLabel),
                minWidth: 150,
                isResizable: true,
                isMultiline: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.subject} />
                ),
                isActive: true,
            },
            {
                key: 'tck__createdAt',
                name: translate(TRANSLATION_KEYS.ColumnCreatedAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.createdAt} />
                ),
                isActive: true,
            },
            {
                key: 'tck__requestedBy',
                name: translate(TRANSLATION_KEYS.ColumnRequestedByLabel),
                minWidth: 100,
                isResizable: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.requestedBy} />
                ),
                isActive: true,
            },
            {
                key: 'tck__status',
                name: translate(TRANSLATION_KEYS.ColumnStatusLabel),
                minWidth: 75,
                maxWidth: 75,
                onRender: (dataItem: Ticket) => (
                    <IAIStatusColumn align="start" status={dataItem.status} />
                ),
                isActive: true,
            },
            {
                key: 'tck__department',
                name: translate(TRANSLATION_KEYS.ColumnDepartmentLabel),
                minWidth: 100,
                isResizable: true,
                isMultiline: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.department?.displayName} />
                ),
                isActive: true,
            },
            {
                key: 'tck__agent',
                name: translate(TRANSLATION_KEYS.ColumnAgentLabel),
                minWidth: 100,
                isResizable: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.agent?.displayName} />
                ),
                isActive: true,
            },
            {
                key: 'tck__priority',
                name: translate(TRANSLATION_KEYS.ColumnPriorityLabel),
                minWidth: 75,
                maxWidth: 75,
                onRender: (dataItem: Ticket) => (
                    <IAIPriorityColumn align="start" priority={dataItem.priority} />
                ),
                isActive: true,
            },
            {
                key: 'tck__solvedInDays',
                name: translate(TRANSLATION_KEYS.ColumnSolvedInDaysLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIStringColumn align="start" text={dataItem.status === 'closed' ? `${dataItem.solvedInDays || 0} ${translate(TRANSLATION_KEYS.RowDaysLabel)}` : ""} />
                ),
                isActive: true,
            },
            {
                key: 'tck__solvedStatus',
                name: translate(TRANSLATION_KEYS.ColumnSolvedStatusLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAISolvedStatusColumn align="start" solvedInDays={dataItem.solvedInDays} departmentId={dataItem.department?.id} />
                ),
                isActive: true,
            },
            {
                key: 'tck__closedAt',
                name: translate(TRANSLATION_KEYS.ColumnClosedAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.closedAt} />
                ),
                isActive: true,
            },
            {
                key: 'tck__canceledAt',
                name: translate(TRANSLATION_KEYS.ColumnCanceledAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.canceledAt} />
                ),
                isActive: true,
            },
            {
                key: 'tck__deletedAt',
                name: translate(TRANSLATION_KEYS.ColumnDeletedAtLabel),
                minWidth: 75,
                maxWidth: 75,
                isCollapsible: true,
                onRender: (dataItem: Ticket) => (
                    <IAIDatetimeColumn align="start" datetime={dataItem.deletedAt} />
                ),
                isActive: true,
            },
            ...propertiesColumns(),
            {
                key: 'tck__actions',
                name: '',
                minWidth: 25,
                maxWidth: 25,
                onRender: (dataItem: Ticket, dataIndex: any) => (
                    <IAIContextualMenuColumn
                        menuItems={_handleMenuItemsByRole(dataItem, dataIndex)}
                    />
                ),
                isLocked: true,
            }
        ];
    }, [properties]);

    useEffect(() => {
        setActiveColumns(dataColumns);
    }, [properties]);

    return (
        <div className={`custom-view-tickets-pivot-container`}>
            <IAIDetailsList
                dataSource={dataSource}
                dataColumns={activeColumns.filter(x => x?.isActive || x?.isLocked)}
                dataLoading={dataLoading}
                commands={[
                    {
                        key: '',
                        disabled: dataLoading,
                        text: translate(TRANSLATION_KEYS.CommandCreateLabel),
                        title: translate(TRANSLATION_KEYS.CommandCreateTooltip),
                        iconProps: { iconName: 'Add' },
                        onClick: _handleCreateAction
                    }
                ]}
                sideCommands={[
                    {
                        key: '',
                        disabled: dataLoading,
                        title: translate(TRANSLATION_KEYS.ColumnAgentLabel),
                        iconProps: { iconName: 'ColumnOptions' },
                        onClick: toggleColumnsRefinementDrawerVisibile
                    },
                ]}
                emptyContentLabel={translate(TRANSLATION_KEYS.DetailsListEmptyContentLabel)}
                onItemInvoked={(dataItem: Ticket) => navigate(RouterSettings.PATHS.dashboard.ticket.edit(dataItem.id!))}
                onSearchPredicate={_handleSearchPredicate}
                exportCommands={onExportTicket && [
                    {
                        key: '',
                        disabled: dataLoading,
                        text: translate(TRANSLATION_KEYS.CommandExportLabel),
                        title: translate(TRANSLATION_KEYS.CommandExportTooltip),
                        iconProps: { iconName: 'ExcelDocument' },
                        onClick: onExportTicket
                    }
                ]}
            />

            <IAIColumnsRefinementDrawer
                disabled={dataLoading}
                drawerVisible={columnsRefinementDrawerVisibile}
                columns={dataColumns}
                onApplyCriteria={(columns) => {
                    setActiveColumns(columns);
                    toggleColumnsRefinementDrawerVisibile();
                }}
                onDrawerDismiss={toggleColumnsRefinementDrawerVisibile}
            />

            <ChangeLogsDrawer
                changeLogs={retrieveChangeLogs}
                disabled={dataLoading || retrieveChangeLogsLoading}
                drawerVisible={changeLogsDrawerVisible}
                onDrawerDismiss={toggleChangeLogsDrawerVisible}
            />

            <DeleteDialog
                disabled={dataLoading || deleteTicketLoading}
                modalVisible={deleteTicketModalVisible}
                onModalConfirm={_handleDeleteTicket}
                onModalDismiss={_handleDismissDeleteAction}
            />
        </div>
    );
}

export default CustomViewTicketsPivot;